/**
 * A colour per seat (H19).
 *
 * Telling people apart in the chat log is the job. On the table a bubble is
 * already beside the face that said it, so colour there is reinforcement; in a
 * list of lines it is the only thing separating "Ann" from "Anna" at a glance.
 *
 * ## Why a fixed palette rather than a computed hue
 *
 * Spreading hues evenly round the wheel is the obvious approach and produces
 * colours nobody picked: yellows that vanish on gold, blues that land on the
 * felt, and two neighbours a colour-blind player cannot separate. Ten values
 * chosen once can be checked once.
 *
 * ## Why colour is never the only signal
 *
 * Every place this is used already carries the name and the avatar. The colour
 * is an accent on a border or a dot - never the text itself, and never the only
 * way to know who spoke. That is what keeps it working in greyscale, and for
 * the roughly one player in twelve who will not see these as ten colours.
 */

/**
 * Ten, because ten is the largest table the seat caps allow.
 *
 * All are light enough to sit on the dark surfaces this app has - the felt, the
 * panel and the page gradient - and are ordered so that adjacent seats, who are
 * next to each other on screen, get colours far apart on the wheel.
 */
const SEAT_COLOURS = [
  '#7dd3fc', // sky
  '#fca5a5', // red
  '#86efac', // green
  '#d8b4fe', // purple
  '#fcd34d', // amber
  '#67e8f9', // cyan
  '#f9a8d4', // pink
  '#bef264', // lime
  '#fdba74', // orange
  '#a5b4fc', // indigo
] as const;

/**
 * This seat's colour.
 *
 * Wraps rather than running out, so a seat number beyond the palette still gets
 * a colour instead of nothing. Seats are 0..9 today and the caps make that the
 * ceiling, but a renderer that returned undefined for seat 10 would be a blank
 * where a person should be.
 *
 * A negative or fractional seat cannot happen from the wire, but `%` on one
 * would give a negative index, so the value is clamped rather than trusted.
 */
export function seatColour(seat: number): string {
  const index = Math.abs(Math.trunc(seat)) % SEAT_COLOURS.length;
  return SEAT_COLOURS[index] as string;
}

export const SEAT_COLOUR_COUNT = SEAT_COLOURS.length;
